import type { ReactNode } from "react";

export function PageHeader({ eyebrow, title, children, action }: { eyebrow?: string; title: string; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className="flex flex-col gap-3 border-b border-border pb-4 md:flex-row md:items-end md:justify-between">
      <div className="max-w-3xl">
        {eyebrow ? <p className="text-xs font-semibold uppercase text-muted-foreground">{eyebrow}</p> : null}
        <h1 className="mt-1 text-xl font-semibold tracking-tight text-foreground">{title}</h1>
        {children ? <p className="mt-1 text-sm leading-5 text-muted-foreground">{children}</p> : null}
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
  );
}

export function Surface({ children, className = "" }: { children: ReactNode; className?: string }) {
  return <section className={`rounded-lg border border-border bg-card p-4 shadow-sm ${className}`}>{children}</section>;
}

export function MetricCard({ label, value, detail }: { label: string; value: string; detail?: string }) {
  return (
    <Surface>
      <p className="text-xs font-medium uppercase text-muted-foreground">{label}</p>
      <p className="mt-1 text-lg font-semibold tabular-nums text-foreground">{value}</p>
      {detail ? <p className="mt-1 text-sm text-muted-foreground">{detail}</p> : null}
    </Surface>
  );
}

// Soft tinted badge. Neutral tone uses semantic tokens (adapts to dark); the status tones carry explicit
// dark: variants so good/warn/bad stay legible on a dark surface.
export function StatusPill({ tone = "neutral", children }: { tone?: "neutral" | "good" | "warn" | "bad"; children: ReactNode }) {
  const toneClass = {
    neutral: "bg-muted text-muted-foreground",
    good: "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300",
    warn: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300",
    bad: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300",
  }[tone];
  return <span className={`inline-flex items-center rounded-md px-2.5 py-0.5 text-xs font-medium ${toneClass}`}>{children}</span>;
}

// A compact label→value stat cell — the "uppercase label + tabular value" pattern that was hand-rolled in a
// dozen places (hero strips, role surfaces, comparison cards). One source of truth for a stat.
export function StatField({ label, value, detail }: { label: string; value: string; detail?: string }) {
  return (
    <div className="min-w-0">
      <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-0.5 truncate text-sm font-semibold tabular-nums text-foreground">{value}</p>
      {detail ? <p className="truncate text-xs text-muted-foreground">{detail}</p> : null}
    </div>
  );
}

// A lightweight section heading inside a Surface or page — title (+ optional caption / right-aligned action)
// on the tight scale, replacing the hand-rolled `<h2 text-sm> + <p text-xs>` headers scattered across pages.
export function SectionHeader({ title, caption, action }: { title: string; caption?: string; action?: ReactNode }) {
  return (
    <div className="mb-2 flex items-end justify-between gap-3">
      <div className="min-w-0">
        <h2 className="text-sm font-semibold text-foreground">{title}</h2>
        {caption ? <p className="mt-0.5 text-xs text-muted-foreground">{caption}</p> : null}
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
  );
}

export function EmptyState({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="rounded-lg border border-dashed border-border bg-muted p-5">
      <p className="font-medium text-foreground">{title}</p>
      <p className="mt-1 text-sm leading-5 text-muted-foreground">{children}</p>
    </div>
  );
}
